import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  useMediaQuery,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ArrowDownward,
  ArrowUpward,
  Warning as WarningIcon,
  SignalCellularAlt as FailoverIcon,
  OpenInNew,
} from '@mui/icons-material';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';

const CARD_BG = 'linear-gradient(145deg, rgba(9, 14, 28, 0.98), rgba(16, 24, 48, 0.95))';
const CARD_BORDER = '1px solid rgba(255, 255, 255, 0.06)';
const SECTION_TITLE_SX = {
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  fontSize: '0.9rem',
};
const LABEL_SX = { color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' };

const getPortSpeedColor = (speedMbps) => {
  if (!speedMbps || speedMbps <= 0) return LABEL_SX.color;
  if (speedMbps <= 10) return '#ff5252'; // 10M or less - red
  if (speedMbps <= 100) return '#ffb74d'; // 100M - amber
  if (speedMbps <= 1000) return '#66bb6a'; // 1G - green
  if (speedMbps >= 2500) return '#ba68c8'; // 2.5G+ - purple
  return LABEL_SX.color;
};

const getWifiGenDisplay = (raw) => {
  if (!raw) {
    return { label: 'Unknown', color: LABEL_SX.color };
  }
  const base = raw.replace(/\s*\(.*?\)\s*/, '').trim(); // strip brackets like (2.4G)
  switch (base) {
    case 'WiFi 4':
      return { label: base, color: '#90caf9' };
    case 'WiFi 5':
      return { label: base, color: '#4caf50' };
    case 'WiFi 6':
      return { label: base, color: '#00e5ff' };
    case 'WiFi 7':
      return { label: base, color: '#ff4081' };
    default:
      return { label: base, color: LABEL_SX.color };
  }
};

const UniFi = ({ apiBaseUrl, token, allowedTabs = [], unifiUrl = 'https://un.raidnet.pl/' }) => {
  const [overview, setOverview] = useState(null);
  const [portsData, setPortsData] = useState(null);
  const [topClients, setTopClients] = useState(null);
  const [wifi, setWifi] = useState(null);
  const [health, setHealth] = useState(null);
  const [haStates, setHaStates] = useState({});
  const [aliases, setAliases] = useState({});
  const [aliasDrafts, setAliasDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const editingPortKeyRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasOverview = useMemo(
    () => allowedTabs.length === 0 || allowedTabs.includes('unifi_overview'),
    [allowedTabs]
  );
  const hasPorts = useMemo(
    () => allowedTabs.length === 0 || allowedTabs.includes('unifi_ports'),
    [allowedTabs]
  );
  const hasTopClients = useMemo(
    () => allowedTabs.length === 0 || allowedTabs.includes('unifi_top_clients'),
    [allowedTabs]
  );
  const hasWifi = useMemo(
    () => allowedTabs.length === 0 || allowedTabs.includes('unifi_wifi'),
    [allowedTabs]
  );

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [];
      if (hasOverview) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/unifi/overview`, { headers: authHeaders })
        );
      } else {
        requests.push(Promise.resolve(null));
      }
      if (hasPorts) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/unifi/ports`, { headers: authHeaders })
        );
      } else {
        requests.push(Promise.resolve(null));
      }
      if (hasTopClients) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/unifi/top-clients?limit=15`, {
            headers: authHeaders,
          })
        );
      } else {
        requests.push(Promise.resolve(null));
      }
      if (hasWifi) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/unifi/wifi`, { headers: authHeaders })
        );
      } else {
        requests.push(Promise.resolve(null));
      }
      if (hasOverview) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/unifi/health`, { headers: authHeaders })
        );
      } else {
        requests.push(Promise.resolve(null));
      }
      // Aliases are lightweight; fetch always when ports are visible
      const aliasesReq = hasPorts
        ? axios.get(`${apiBaseUrl}/api/unifi/port-aliases`, { headers: authHeaders })
        : Promise.resolve(null);

      // Home Assistant entity states (for WAN latency cards)
      const haReq = axios
        .get(`${apiBaseUrl}/api/homeassistant/entities`, { headers: authHeaders })
        .catch(() => null);

      const [overviewRes, portsRes, topRes, wifiRes, healthRes, aliasesRes, haRes] =
        await Promise.all([
          requests[0],
          requests[1],
          requests[2],
          requests[3],
          requests[4],
          aliasesReq,
          haReq,
        ]);

      if (overviewRes && overviewRes.data) setOverview(overviewRes.data);
      if (portsRes && portsRes.data) {
        setPortsData(portsRes.data);
        if (portsRes.data.aliases) {
          setAliases(portsRes.data.aliases);
          setAliasDrafts((prev) => {
            const next = { ...portsRes.data.aliases };
            const key = editingPortKeyRef.current;
            if (key && prev[key] !== undefined) next[key] = prev[key];
            return next;
          });
        }
      }
      if (topRes && topRes.data) setTopClients(topRes.data);
      if (wifiRes && wifiRes.data) setWifi(wifiRes.data);
      if (healthRes && healthRes.data) setHealth(healthRes.data);
      if (aliasesRes && aliasesRes.data && aliasesRes.data.aliases) {
        setAliases(aliasesRes.data.aliases);
        setAliasDrafts((prev) => {
          const next = { ...aliasesRes.data.aliases };
          const key = editingPortKeyRef.current;
          if (key && prev[key] !== undefined) next[key] = prev[key];
          return next;
        });
      }
      if (haRes && haRes.data && Array.isArray(haRes.data)) {
        const nextStates = {};
        haRes.data.forEach((entity) => {
          if (entity && entity.entity_id) {
            nextStates[entity.entity_id] = entity;
          }
        });
        setHaStates(nextStates);
      }
    } catch (err) {
      // Swallow per-request errors; UniFi may be temporarily offline
      // eslint-disable-next-line no-console
      console.error('UniFi fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, authHeaders, hasOverview, hasPorts, hasTopClients, hasWifi]);

  useEffect(() => {
    if (!token) return;
    fetchOnce();
    const interval = setInterval(fetchOnce, 5000);
    return () => clearInterval(interval);
  }, [token, fetchOnce]);

  const handleAliasChange = (key, value) => {
    setAliasDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const handleAliasSave = async (deviceMac, portIndex) => {
    const key = `${deviceMac.toLowerCase()}:${portIndex}`;
    const alias = aliasDrafts[key] ?? '';
    try {
      const payload = { alias };
      const res = await axios.put(
        `${apiBaseUrl}/api/unifi/port-aliases/${encodeURIComponent(deviceMac)}/${portIndex}`,
        payload,
        { headers: authHeaders }
      );
      if (res.data && res.data.aliases) {
        setAliases(res.data.aliases);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save UniFi port alias', err);
    }
  };

  const renderGatewayCard = () => {
    if (!overview || !overview.gateway) return null;
    const gw = overview.gateway;
    const cpu = gw.cpu_percent;
    const ram = gw.ram_percent;
    const temp = gw.cpu_temp;
    const uptime = gw.uptime_hours;

    let status = 'Healthy';
    let statusColor = 'success';

    if (
      (cpu != null && cpu > 80) ||
      (ram != null && ram > 80) ||
      (temp != null && temp > 75)
    ) {
      status = 'Warning';
      statusColor = 'warning';
    }
    if (
      (cpu != null && cpu > 90) ||
      (ram != null && ram > 90) ||
      (temp != null && temp > 85)
    ) {
      status = 'Critical';
      statusColor = 'error';
    }

    return (
      <Card
        sx={{
          height: '100%',
          background: CARD_BG,
          border: CARD_BORDER,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              gap: { xs: 1, sm: 0 },
              mb: { xs: 1, sm: 1.5 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/unifi-controller.png"
                alt="UniFi Gateway"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  boxShadow: '0 0 14px rgba(0, 212, 255, 0.8)',
                  objectFit: 'contain',
                }}
              />
              <Box>
                <Typography variant="h6" sx={SECTION_TITLE_SX}>
                  UniFi Hardware Health
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  {gw.name} ({gw.model})
                </Typography>
              </Box>
            </Box>
            <Chip
              label={status}
              color={statusColor}
              size="small"
              sx={{
                fontWeight: 600,
                alignSelf: { xs: 'flex-start', sm: 'auto' },
                mt: { xs: 0.5, sm: 0 },
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ mb: 1, ...LABEL_SX }}>
            IP: {gw.ip || 'n/a'} · Uptime:{' '}
            {uptime != null ? `${uptime.toFixed?.(1) ?? uptime} h` : 'n/a'}
          </Typography>

          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={LABEL_SX}>
                  CPU
                </Typography>
                <Typography variant="caption" sx={LABEL_SX}>
                  {cpu != null ? `${cpu.toFixed?.(0) ?? cpu}%` : 'n/a'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, cpu || 0)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #00d4ff, #2979ff)',
                  },
                }}
              />
            </Box>

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={LABEL_SX}>
                  RAM
                </Typography>
                <Typography variant="caption" sx={LABEL_SX}>
                  {ram != null ? `${ram.toFixed?.(0) ?? ram}%` : 'n/a'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, ram || 0)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #ffd54f, #ff9800)',
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
            >
              <Typography variant="caption" sx={LABEL_SX}>
                CPU Temp:{' '}
                {temp != null ? `${temp.toFixed?.(0) ?? temp} °C` : 'n/a'}
              </Typography>
              <Typography variant="caption" sx={LABEL_SX}>
                Uptime:{' '}
                {uptime != null ? `${uptime.toFixed?.(1) ?? uptime} h` : 'n/a'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const failoverActive = overview?.failover_active === true;
  const activeWan = overview?.active_wan || 'wan1';

  const renderWanCard = (wan, index) => {
    if (!wan) return null;
    const hasTraffic = (wan.rx_mbps || 0) > 0 || (wan.tx_mbps || 0) > 0;
    const online =
      (wan.status || '').toString().toLowerCase() === 'online' || hasTraffic;
    const isWan1 = index === 0;
    const isActiveFailover = failoverActive && !isWan1;
    const isPrimaryActive = !failoverActive && isWan1;
    const statusLabel = isActiveFailover
      ? 'Active (Failover)'
      : isPrimaryActive
        ? 'Primary'
        : online
          ? 'Online'
          : wan.status || 'Offline';
    const iconSrc = isWan1 ? '/Ubiquity/play.png' : '/Ubiquity/orange.png';
    const labelPrefix = isWan1 ? 'WAN1' : 'WAN2';

    const getLatencyForEntity = (entityId) => {
      const entity = haStates[entityId];
      if (!entity || entity.state == null || entity.state === 'unavailable') {
        return 'n/a';
      }
      const num = Number(entity.state);
      const value =
        Number.isFinite(num) && entity.state !== ''
          ? num.toFixed(0).replace(/\.0$/, '')
          : entity.state;
      const unit = entity.attributes?.unit_of_measurement || 'ms';
      return `${value} ${unit}`;
    };

    const cloudflareEntityId = isWan1
      ? 'sensor.cloud_gateway_ultra_cloudflare_wan_latency'
      : 'sensor.cloud_gateway_ultra_cloudflare_wan2_latency';
    const googleEntityId = isWan1
      ? 'sensor.cloud_gateway_ultra_google_wan_latency'
      : 'sensor.cloud_gateway_ultra_google_wan2_latency';
    const microsoftEntityId = isWan1
      ? 'sensor.cloud_gateway_ultra_microsoft_wan_latency'
      : 'sensor.cloud_gateway_ultra_microsoft_wan2_latency';

    return (
      <Card
        sx={{
          height: '100%',
          background: CARD_BG,
          border: CARD_BORDER,
          ...(isActiveFailover && {
            border: '2px solid rgba(255, 152, 0, 0.7)',
            boxShadow: '0 0 20px rgba(255, 152, 0, 0.25)',
          }),
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              gap: { xs: 1, sm: 0 },
              mb: { xs: 1, sm: 1.5 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={iconSrc}
                alt={labelPrefix}
                sx={{ width: 28, height: 28, objectFit: 'contain' }}
              />
              <Box>
                <Typography variant="subtitle1" sx={SECTION_TITLE_SX}>
                  {labelPrefix} | {wan.name}
                </Typography>
                <Typography variant="caption" sx={LABEL_SX}>
                  IP: {wan.ip || 'n/a'}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={statusLabel}
              color={isActiveFailover ? 'warning' : online ? 'success' : 'default'}
              size="small"
              icon={isActiveFailover ? <FailoverIcon sx={{ fontSize: 16 }} /> : undefined}
              sx={{
                fontWeight: 600,
                alignSelf: { xs: 'flex-start', sm: 'auto' },
                mt: { xs: 0.5, sm: 0 },
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ mb: 0.5, ...LABEL_SX }}>
            Latency:{' '}
            {wan.latency_ms !== null && wan.latency_ms !== undefined
              ? `${wan.latency_ms} ms`
              : 'n/a'}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 0.75,
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowDownward sx={{ fontSize: 18, color: '#ff4081' }} />
              <Typography
                variant="body2"
                sx={{ color: '#ff4081', fontWeight: 600 }}
              >
                {(wan.rx_mbps ?? 0).toFixed?.(1) ?? wan.rx_mbps} Mbps
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowUpward sx={{ fontSize: 18, color: '#00e676' }} />
              <Typography
                variant="body2"
                sx={{ color: '#00e676', fontWeight: 600 }}
              >
                {(wan.tx_mbps ?? 0).toFixed?.(1) ?? wan.tx_mbps} Mbps
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ((wan.rx_mbps || 0) / 800) * 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.05)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #ff4081, #f50057)',
                  borderRadius: 3,
                },
              }}
            />
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ((wan.tx_mbps || 0) / 40) * 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.05)',
                mt: 0.75,
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #00e676, #69f0ae)',
                  borderRadius: 3,
                },
              }}
            />
          </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mt: { xs: 2, sm: 2.5 },
          }}
        >
          {/* First row: Cloudflare and Google side by side */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 1.5 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flex: { xs: '1 1 100%', sm: '1 1 50%' },
              }}
            >
              <Box
                component="img"
                src="/cloudflare_latency.svg"
                alt="Cloudflare latency"
                sx={{
                  width: { xs: 16, sm: 18 },
                  height: { xs: 16, sm: 18 },
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="caption"
                sx={{ ...LABEL_SX, flex: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              >
                Cloudflare
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                {getLatencyForEntity(cloudflareEntityId)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flex: { xs: '1 1 100%', sm: '1 1 50%' },
              }}
            >
              <Box
                component="img"
                src="/google_latency.png"
                alt="Google latency"
                sx={{
                  width: { xs: 16, sm: 18 },
                  height: { xs: 16, sm: 18 },
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="caption"
                sx={{ ...LABEL_SX, flex: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              >
                Google
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                {getLatencyForEntity(googleEntityId)}
              </Typography>
            </Box>
          </Box>

          {/* Second row: Microsoft below Cloudflare */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              width: { xs: '100%', sm: '49%' },
            }}
          >
            <Box
              component="img"
              src="/ms_latency.png"
              alt="Microsoft latency"
              sx={{
                width: { xs: 16, sm: 18 },
                height: { xs: 16, sm: 18 },
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="caption"
              sx={{ ...LABEL_SX, flex: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              Microsoft
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}
            >
              {getLatencyForEntity(microsoftEntityId)}
            </Typography>
          </Box>
        </Box>
        </CardContent>
      </Card>
    );
  };

  const getDeviceIconSrc = (device) => {
    const model = (device.model || '').toUpperCase();
    const type = (device.type || '').toLowerCase();

    // Switches (USW Flex 2.5G)
    if (type === 'usw' || model.includes('USW')) {
      return '/Ubiquity/USW%20Flex%202.5G.avif';
    }
    // APs (U7 Pro)
    if (type === 'uap' || model.includes('U7PRO') || model.includes('U7 PRO')) {
      return '/Ubiquity/U7%20Pro.avif';
    }
    // Cloud Gateway / UDR
    if (model.includes('UDR') || model.includes('UDRULT') || type === 'udm') {
      return '/Ubiquity/CGU.avif';
    }
    return '/unifi-controller.png';
  };

  const formatUptime = (uptimeHours) => {
    if (uptimeHours == null) return 'N/A';
    const totalHours = Number(uptimeHours) || 0;
    if (totalHours < 24) {
      return `${totalHours.toFixed(1)} h`;
    }
    const days = Math.floor(totalHours / 24);
    const hours = Math.floor(totalHours - days * 24);
    if (hours <= 0) {
      return `${days}d`;
    }
    return `${days}d ${hours}h`;
  };

  const renderHealthTable = () => {
    if (!health || !Array.isArray(health.devices) || health.devices.length === 0) {
      return null;
    }

    return (
      <Card
        sx={{
          background: CARD_BG,
          border: CARD_BORDER,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/Ubiquity/CGU.avif"
                alt="UniFi Health"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  objectFit: 'contain',
                  boxShadow: '0 0 12px rgba(0, 212, 255, 0.7)',
                }}
              />
              <Box>
                <Typography variant="h6" sx={SECTION_TITLE_SX}>
                  System Health
                </Typography>
                <Typography variant="caption" sx={LABEL_SX}>
                  UniFi gateway, switches and access points
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Refresh now">
              <IconButton size="small" onClick={fetchOnce} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              overflowX: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={LABEL_SX}>Device</TableCell>
                  <TableCell sx={LABEL_SX}>Model</TableCell>
                  <TableCell align="right">CPU %</TableCell>
                  <TableCell align="right">RAM %</TableCell>
                  <TableCell align="right">Temp</TableCell>
                  <TableCell align="right">Uptime</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {health.devices.map((dev) => {
                  const cpu = dev.cpu_percent;
                  const ram = dev.ram_percent;
                  const temp = dev.temp_c;

                  let color = 'inherit';
                  if (
                    (cpu != null && cpu > 90) ||
                    (ram != null && ram > 90) ||
                    (temp != null && temp > 85)
                  ) {
                    color = '#ff5252';
                  } else if (
                    (cpu != null && cpu > 80) ||
                    (ram != null && ram > 80) ||
                    (temp != null && temp > 75)
                  ) {
                    color = '#ffb74d';
                  }

                  return (
                    <TableRow key={`${dev.name}-${dev.model}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            component="img"
                            src={getDeviceIconSrc(dev)}
                            alt={dev.model || dev.type || dev.name}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              objectFit: 'contain',
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color, fontWeight: 500 }}
                          >
                            {dev.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color }}>
                          {dev.model || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color }}>
                          {cpu != null ? cpu.toFixed?.(1) ?? cpu : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color }}>
                          {ram != null ? ram.toFixed?.(1) ?? ram : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color }}>
                          {temp != null
                            ? `${temp.toFixed?.(1) ?? temp} °C`
                            : '---'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color }}>
                          {formatUptime(dev.uptime_hours)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderLanTable = () => {
    if (!overview || !overview.lan_networks || overview.lan_networks.length === 0) {
      return null;
    }
    return (
      <Card
        sx={{
          background: CARD_BG,
          border: CARD_BORDER,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={SECTION_TITLE_SX}>
              LAN Networks
            </Typography>
            <Tooltip title="Refresh now">
              <IconButton size="small" onClick={fetchOnce} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              overflowX: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={LABEL_SX}>Name</TableCell>
                  <TableCell sx={LABEL_SX}>IP</TableCell>
                  <TableCell align="right" sx={LABEL_SX}>
                    Clients
                  </TableCell>
                  <TableCell align="right" sx={LABEL_SX}>
                    ↓ Mbps
                  </TableCell>
                  <TableCell align="right" sx={LABEL_SX}>
                    ↑ Mbps
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.lan_networks.map((net) => (
                  <TableRow key={net.name || net.ip}>
                    <TableCell>{net.name}</TableCell>
                    <TableCell>{net.ip}</TableCell>
                    <TableCell align="right">{net.num_clients}</TableCell>
                    <TableCell align="right">{net.rx_mbps}</TableCell>
                    <TableCell align="right">{net.tx_mbps}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderTopClients = () => {
    if (!topClients || !topClients.clients || topClients.clients.length === 0) {
      return null;
    }
    const allClients = topClients.clients || [];
    const visibleClients = isMobile ? allClients.slice(0, 10) : allClients;
    return (
      <Card
        sx={{
          background: CARD_BG,
          border: CARD_BORDER,
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, ...SECTION_TITLE_SX }}>
            Top Clients (Download)
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              overflowX: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={LABEL_SX}>Name</TableCell>
                  <TableCell sx={LABEL_SX}>IP</TableCell>
                  <TableCell align="right" sx={LABEL_SX}>
                    ↓ Mbps
                  </TableCell>
                  <TableCell align="right" sx={LABEL_SX}>
                    ↑ Mbps
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleClients.map((c) => (
                  <TableRow key={c.mac}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.ip}</TableCell>
                    <TableCell align="right">{c.rx_mbps}</TableCell>
                    <TableCell align="right">{c.tx_mbps}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, ...LABEL_SX }}
          >
            Total clients: {topClients.total_clients}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderWifi = () => {
    if (!wifi || !wifi.wifi || wifi.wifi.length === 0) {
      return null;
    }
    const activeWifi = wifi.wifi.filter((wlan) => wlan.enabled);
    if (activeWifi.length === 0) {
      return null;
    }
    return (
      <Card
        sx={{
          background: CARD_BG,
          border: CARD_BORDER,
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5, ...SECTION_TITLE_SX }}>
            WiFi SSIDs
          </Typography>
          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
              overflowX: 'auto',
              paddingBottom: theme.spacing(2),
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(0.5),
              marginLeft: theme.spacing(1),
              WebkitOverflowScrolling: 'touch',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              [theme.breakpoints.down('sm')]: {
                margin: theme.spacing(0, -2),
                marginLeft: theme.spacing(2),
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
                '& > *': {
                  flex: '0 0 85%',
                  scrollSnapAlign: 'start',
                  minWidth: '280px',
                },
              },
              [theme.breakpoints.up('sm')]: {
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                overflowX: 'visible',
                paddingBottom: 0,
                margin: 0,
                marginLeft: theme.spacing(1),
                paddingLeft: 0,
                paddingRight: 0,
              },
            })}
          >
            {activeWifi.map((wlan) => (
              <Box
                key={wlan.name}
                sx={{
                  mb: 1.5,
                  pb: 1.5,
                  borderRadius: { xs: 2, sm: 0 },
                  border: { xs: '1px solid rgba(255,255,255,0.08)', sm: 'none' },
                  background: { xs: 'rgba(0,0,0,0.18)', sm: 'transparent' },
                  px: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {wlan.name}
                  </Typography>
                  <Chip
                    label={wlan.enabled ? 'Active' : 'Offline'}
                    color={wlan.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" sx={LABEL_SX}>
                  Bands: {wlan.bands.join(', ') || 'n/a'} | Security: {wlan.wpa_mode || 'n/a'} | PMF:{' '}
                  {wlan.pmf_mode}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, ...LABEL_SX }}>
                  Clients: {wlan.client_count} | Traffic: {wlan.total_traffic_human}
                </Typography>
                {wlan.top_clients && wlan.top_clients.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={LABEL_SX}>
                      Top consumers:
                    </Typography>
                    {wlan.top_clients.map((c) => {
                      const wifiDisplay = getWifiGenDisplay(c.wifi_gen);
                      return (
                        <Box
                          key={c.name + c.ap_name}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 1 },
                            rowGap: { xs: 0.25, sm: 0.5 },
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, minWidth: { xs: '50%', sm: 0 } }}
                          >
                            {c.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ width: { xs: '50%', sm: 80 } }}
                          >
                            {c.signal_dbm} dBm
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ width: { xs: '50%', sm: 80 }, color: wifiDisplay.color }}
                          >
                            {wifiDisplay.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}
                          >
                            AP: {c.ap_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ width: { xs: '100%', sm: 110 } }}
                          >
                            {c.total_usage_human}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderPorts = () => {
    if (!portsData || !portsData.devices || portsData.devices.length === 0) {
      return null;
    }
    const sortedDevices = [...portsData.devices].sort((a, b) => {
      const aIsGateway = (a.type || '').toLowerCase() === 'udm';
      const bIsGateway = (b.type || '').toLowerCase() === 'udm';
      if (aIsGateway && !bIsGateway) return -1;
      if (!aIsGateway && bIsGateway) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return (
      <Card
        sx={{
          background: CARD_BG,
          border: CARD_BORDER,
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5, ...SECTION_TITLE_SX }}>
            Ports (CGU + Switch)
          </Typography>
          {sortedDevices.map((dev) => (
            <Box key={dev.mac} sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={getDeviceIconSrc(dev)}
                  alt={dev.name || dev.type}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    objectFit: 'contain',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, ...LABEL_SX }}
                >
                  {dev.name} ({dev.type})
                </Typography>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  background: 'transparent',
                  boxShadow: 'none',
                  overflowX: 'auto',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Port</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>Device / Link</TableCell>
                      <TableCell align="right">Speed</TableCell>
                      <TableCell align="right">↓ Live Mbps</TableCell>
                      <TableCell align="right">↑ Live Mbps</TableCell>
                      <TableCell align="right">↓ Total</TableCell>
                      <TableCell align="right">↑ Total</TableCell>
                      <TableCell align="right">Save</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dev.ports.map((p) => {
                      const key = `${dev.mac}:${p.port_index}`;
                      const draft = aliasDrafts[key] ?? p.alias ?? p.default_name ?? '';
                      const isBusy =
                        (p.rx_mbps_live || 0) > 5 || (p.tx_mbps_live || 0) > 5;
                      return (
                        <TableRow
                          key={key}
                          sx={
                            isBusy
                              ? {
                                  backgroundColor: 'rgba(0, 212, 255, 0.06)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 212, 255, 0.12)',
                                  },
                                  transition: 'background-color 0.3s ease',
                                }
                              : undefined
                          }
                        >
                          <TableCell>{p.port_index}</TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              value={draft}
                              onChange={(e) => handleAliasChange(key, e.target.value)}
                              onFocus={() => { editingPortKeyRef.current = key; }}
                              onBlur={() => { editingPortKeyRef.current = null; }}
                              placeholder={p.default_name || 'Device / Link'}
                              sx={{
                                width: { xs: '100%', sm: 'auto' },
                                '& .MuiInputBase-input': { color: 'rgba(255,255,255,0.9)' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255,255,255,0.2)',
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: getPortSpeedColor(p.link_speed_mbps),
                              fontWeight: 600,
                            }}
                          >
                            {p.link_speed_mbps ? `${p.link_speed_mbps}M` : '-'}
                          </TableCell>
                          <TableCell align="right">{p.rx_mbps_live}</TableCell>
                          <TableCell align="right">{p.tx_mbps_live}</TableCell>
                          <TableCell align="right">{p.rx_total_human}</TableCell>
                          <TableCell align="right">{p.tx_total_human}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Save Device / Link">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAliasSave(dev.mac, p.port_index)}
                                  disabled={loading}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(255, 64, 129, 0.15))',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src="/unifi.png"
              alt="UniFi"
              sx={{ width: 36, height: 36, objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
              UniFi
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <OpenInNew sx={{ fontSize: 14 }} />{' '}
              <Link
                href={unifiUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#00d4ff' } }}
              >
                {unifiUrl}
              </Link>
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={fetchOnce}
            disabled={loading}
            sx={{
              color: '#00d4ff',
              background: 'rgba(0, 212, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.1)',
              '&:hover': { background: 'rgba(0, 212, 255, 0.2)', borderColor: 'rgba(0, 212, 255, 0.3)' },
              '&.Mui-disabled': { background: 'rgba(0, 212, 255, 0.05)' },
            }}
          >
            <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {hasOverview && failoverActive && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 87, 34, 0.1))',
            border: '1px solid rgba(255, 152, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 0 24px rgba(255, 152, 0, 0.15)',
          }}
        >
          <FailoverIcon sx={{ fontSize: 36, color: 'rgba(255, 152, 0, 0.9)' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.95)' }}>
              Failover WAN active
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
              WAN1 is down or inactive. Traffic is currently using WAN2 (4G failover).
            </Typography>
          </Box>
          <Chip
            icon={<WarningIcon sx={{ fontSize: 18 }} />}
            label="4G Failover"
            color="warning"
            size="medium"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      <Grid container spacing={3}>
        {hasOverview && (
          <Grid item xs={12}>
            <Box
              sx={(theme) => ({
                display: 'flex',
                gap: theme.spacing(2),
                overflowX: 'auto',
                paddingBottom: theme.spacing(2),
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(0.5),
                marginLeft: theme.spacing(1),
                WebkitOverflowScrolling: 'touch',
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                [theme.breakpoints.down('sm')]: {
                  margin: theme.spacing(0, -2),
                  marginLeft: theme.spacing(2),
                  paddingLeft: theme.spacing(2),
                  paddingRight: theme.spacing(2),
                  '& > *': {
                    flex: '0 0 85%',
                    scrollSnapAlign: 'start',
                    minWidth: '280px',
                  },
                },
                [theme.breakpoints.up('sm')]: {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: theme.spacing(3),
                  overflowX: 'visible',
                  paddingBottom: 0,
                  margin: 0,
                  marginLeft: theme.spacing(1),
                  paddingLeft: 0,
                  paddingRight: 0,
                },
              })}
            >
              {overview &&
                overview.wan &&
                overview.wan[0] &&
                renderWanCard(overview.wan[0], 0)}
              {overview &&
                overview.wan &&
                overview.wan[1] &&
                renderWanCard(overview.wan[1], 1)}
              {renderGatewayCard()}
            </Box>
          </Grid>
        )}

        {hasOverview && (
          <>
            <Grid item xs={12} md={6}>
              {renderHealthTable()}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLanTable()}
            </Grid>
          </>
        )}

        {(hasTopClients || hasWifi) && (
          <>
            {hasTopClients && (
              <Grid item xs={12} md={6}>
                {renderTopClients()}
              </Grid>
            )}
            {hasWifi && (
              <Grid item xs={12} md={6}>
                {renderWifi()}
              </Grid>
            )}
          </>
        )}

        {hasPorts && (
          <Grid item xs={12}>
            {renderPorts()}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UniFi;

